import { Vector3 } from 'three';

import type { Skeleton } from '../skeleton/core';
import {
  APPENDAGE_HINTS,
  ChainMapping,
  CHAIN_DEFINITIONS,
  matchAppendageTypeFromName,
  type AppendageType,
} from '../schema/mapping';
import { getChainRoleAssignments } from '../mapping/chainRoleAssignments';
import type { ScoreBreakdown } from './scoring';

import { type ChainResult, RecognizerConfig } from './config';
import {
  type ExtraArmCandidate,
  findExtraArmCandidates,
} from './phases/arms';
import { classifyLegType, trimPathAtFoot } from './phases/legs';
import { pruneInlineTwistBones } from './helpers';
import { detectFingers } from './phases/fingers';

// ═══════════════════════════════════════════════════════════════════════
// DETECTED FEATURE
// ═══════════════════════════════════════════════════════════════════════

export interface DetectedFeature {
  label: string;
  description: string;
}

// ═══════════════════════════════════════════════════════════════════════
// AUGMENTATION CONTEXT
// ═══════════════════════════════════════════════════════════════════════

export interface AugmentationContext {
  skel: Skeleton;
  chains: Record<string, string[]>;
  chainResults: Record<string, ChainResult>;
  chainMappings: Record<string, [number, number, string | null]>;
  roleToBone: Record<string, string>;
  roleToConfidence: Record<string, number>;
  explain: Record<string, ScoreBreakdown>;
  baseConfidence: number;
  warnings: string[];
  detectedFeatures: DetectedFeature[];

  assignRole(role: string, boneName: string): void;
  boneIndex(name: string): number | null;
  boneName(idx: number): string;
  addFeature(label: string, description: string): void;
  log(msg: string): void;
}

// ═══════════════════════════════════════════════════════════════════════
// SHARED HELPERS
// ═══════════════════════════════════════════════════════════════════════

function findNextStructuralBone(
  skel: Skeleton,
  startIdx: number,
  preferCandidate?: (boneIdx: number) => boolean,
): { boneIdx: number | null; matchedPreference: boolean } {
  const queue = [...skel.bone(startIdx).children];
  const visited = new Set<number>();
  let best: number | null = null;
  let bestDepth = -1;
  let bestPreferred: number | null = null;
  let bestPreferredDepth = -1;

  while (queue.length > 0) {
    const idx = queue.shift()!;
    if (visited.has(idx)) continue;
    visited.add(idx);

    const bone = skel.bone(idx);
    if (!bone.auxiliary) {
      let depth = 0;
      let cur = idx;
      for (let i = 0; i < 5; i++) {
        const children = skel.bone(cur).children.filter((c) => !skel.bone(c).auxiliary);
        if (children.length === 0) break;
        cur = children[0];
        depth++;
      }
      if (preferCandidate?.(idx) && depth > bestPreferredDepth) {
        bestPreferredDepth = depth;
        bestPreferred = idx;
      }
      if (depth > bestDepth) {
        bestDepth = depth;
        best = idx;
      }
    } else {
      queue.push(...bone.children);
    }
  }

  return {
    boneIdx: bestPreferred ?? best,
    matchedPreference: bestPreferred !== null,
  };
}

function extendChainDownward(
  skel: Skeleton,
  chainIndices: number[],
  maxExtra = 4,
): number[] {
  const extended = [...chainIndices];
  let current = extended[extended.length - 1];

  for (let i = 0; i < maxExtra; i++) {
    const { boneIdx: nextBone } = findNextStructuralBone(skel, current);
    if (nextBone === null) break;

    extended.push(nextBone);
    current = nextBone;

    const nonAuxChildren = skel.bone(nextBone).children.filter(
      (c) => !skel.bone(c).auxiliary,
    );
    if (nonAuxChildren.length >= 3) break;
  }

  return extended;
}

// ═══════════════════════════════════════════════════════════════════════
// DIGITIGRADE AUGMENTATION
// ═══════════════════════════════════════════════════════════════════════

const LEG_JOINT_HINTS = ['knee', 'calf', 'shin', 'lowerleg', 'tibia', 'rodilla', 'pierna', 'genou', 'jambe'];

function findLowerLeg(ctx: AugmentationContext, candidates: string[]): string {
  for (const bn of candidates) {
    const idx = ctx.skel.resolveBoneRef(bn, { namespace: 'internal' });
    if (idx === null) continue;
    const nameLower = ctx.skel.bone(idx).name.toLowerCase();
    if (LEG_JOINT_HINTS.some((hint) => nameLower.includes(hint))) return bn;
  }

  let bestBone = candidates[candidates.length - 1];
  let bestAngle = 0.0;
  const skel = ctx.skel;

  for (const bn of candidates) {
    const idx = skel.resolveBoneRef(bn, { namespace: 'internal' });
    if (idx === null) continue;
    const bone = skel.bone(idx);
    if (bone.parent === null) continue;
    const parent = skel.bone(bone.parent);

    const boneDir = bone.direction;
    const parentDir = parent.direction;
    const n1 = boneDir.length();
    const n2 = parentDir.length();
    if (n1 > 1e-8 && n2 > 1e-8) {
      const dot = boneDir.clone().divideScalar(n1).dot(parentDir.clone().divideScalar(n2));
      const angle = (Math.acos(Math.max(-1.0, Math.min(1.0, dot))) * 180) / Math.PI;
      if (angle > bestAngle) {
        bestAngle = angle;
        bestBone = bn;
      }
    }
  }

  return bestBone;
}

function applyDigitigrade(
  ctx: AugmentationContext,
  side: string,
  prunedChain: number[],
): void {
  const skel = ctx.skel;
  const legChainName = `${side}Leg`;
  const toeChainName = `${side}Toe`;

  let digiChain: number[];
  let toeBone: number | null;

  if (prunedChain.length >= 5) {
    digiChain = prunedChain.slice(0, -1);
    toeBone = prunedChain[prunedChain.length - 1];
  } else {
    digiChain = [...prunedChain];
    toeBone = null;
  }

  const digiChainNames = digiChain.map((idx) => skel.refForBoneIndex(idx));
  const ikGoal = ctx.chainResults[legChainName]?.ikGoal ?? null;

  ctx.chains[legChainName] = digiChainNames;
  ctx.chainResults[legChainName] = new ChainMapping({
    name: legChainName,
    startBone: digiChainNames[0],
    endBone: digiChainNames[digiChainNames.length - 1],
    boneNames: digiChainNames,
    confidence: ctx.baseConfidence,
    ikGoal,
    legType: 'digitigrade',
  });
  ctx.chainMappings[legChainName] = [
    digiChain[0],
    digiChain[digiChain.length - 1],
    ikGoal,
  ];

  if (toeBone !== null) {
    const toeBoneName = skel.refForBoneIndex(toeBone);
    ctx.chains[toeChainName] = [toeBoneName];
    ctx.chainResults[toeChainName] = new ChainMapping({
      name: toeChainName,
      startBone: toeBoneName,
      endBone: toeBoneName,
      boneNames: [toeBoneName],
      confidence: ctx.baseConfidence,
    });
    ctx.chainMappings[toeChainName] = [toeBone, toeBone, null];
  }

  const legBones = digiChainNames;

  const footRole = `${side}Foot`;
  if (!(footRole in ctx.roleToBone)) {
    ctx.assignRole(footRole, legBones[legBones.length - 1]);
  }

  const hockRole = `${side}Hock`;
  if (!(hockRole in ctx.roleToBone)) {
    ctx.assignRole(hockRole, legBones[legBones.length - 2]);
  }

  const lowerLegRole = `${side}LowerLeg`;
  if (!(lowerLegRole in ctx.roleToBone)) {
    const candidates = legBones.slice(1, -2);
    if (candidates.length > 0) {
      ctx.assignRole(lowerLegRole, findLowerLeg(ctx, candidates));
    } else if (legBones.length >= 3) {
      ctx.assignRole(lowerLegRole, legBones[1]);
    }
  }

  if (toeBone !== null) {
    const toeRole = `${side}Toe`;
    if (!(toeRole in ctx.roleToBone)) {
      ctx.assignRole(toeRole, skel.refForBoneIndex(toeBone));
    }
  }

  ctx.addFeature(
    'Digitigrade Legs',
    'Detected hind-leg (digitigrade) anatomy: the leg chain was ' +
      'extended with a Hock joint between LowerLeg and Foot, producing ' +
      'an UpperLeg \u2192 LowerLeg \u2192 Hock \u2192 Foot \u2192 Toe chain.',
  );
}

function detectDigitigradeLegs(ctx: AugmentationContext): void {
  const downAxis = new Vector3(0.0, -1.0, 0.0);

  for (const side of ['Left', 'Right']) {
    const legChainName = `${side}Leg`;
    const toeChainName = `${side}Toe`;

    if (!(legChainName in ctx.chains)) continue;

    const legChainNames = ctx.chains[legChainName];
    const legChainIndices = legChainNames
      .map((n) => ctx.boneIndex(n))
      .filter((i): i is number => i !== null);

    if (legChainIndices.length === 0) continue;

    const extendedIndices = extendChainDownward(ctx.skel, legChainIndices, 4);

    if (extendedIndices.length <= legChainIndices.length) continue;

    const prunedChain = pruneInlineTwistBones(ctx.skel, extendedIndices);
    const normalizedChain = trimPathAtFoot(ctx.skel, prunedChain);
    const legType = classifyLegType(ctx.skel, normalizedChain, downAxis);

    if (legType !== 'digitigrade' || normalizedChain.length < 4) continue;

    applyDigitigrade(ctx, side, normalizedChain);
  }
}

// ═══════════════════════════════════════════════════════════════════════
// EXTRA ARMS AUGMENTATION
// ═══════════════════════════════════════════════════════════════════════

function collectClaimedIndices(ctx: AugmentationContext): Set<number> {
  const claimed = new Set<number>();
  for (const chainBones of Object.values(ctx.chains)) {
    for (const bname of chainBones) {
      const idx = ctx.boneIndex(bname);
      if (idx !== null) claimed.add(idx);
    }
  }
  return claimed;
}

function collectTorsoIndices(ctx: AugmentationContext): number[] {
  const torsoSet = new Set<number>();
  const torso: number[] = [];

  function add(idx: number) {
    if (!torsoSet.has(idx)) {
      torsoSet.add(idx);
      torso.push(idx);
    }
  }

  for (const chainName of ['Pelvis', 'Spine']) {
    if (chainName in ctx.chains) {
      for (const bname of ctx.chains[chainName]) {
        const idx = ctx.boneIndex(bname);
        if (idx !== null) add(idx);
      }
    }
  }

  for (const chainName of ['LeftClavicle', 'RightClavicle', 'LeftArm', 'RightArm']) {
    if (!(chainName in ctx.chains)) continue;
    const firstBone = ctx.chains[chainName][0];
    let idx = ctx.boneIndex(firstBone);
    if (idx === null) continue;
    let parent = ctx.skel.bone(idx).parent;
    while (parent !== null) {
      add(parent);
      if (
        torsoSet.has(parent) &&
        ctx.skel.bone(parent).parent !== null &&
        torsoSet.has(ctx.skel.bone(parent).parent!)
      ) {
        break;
      }
      parent = ctx.skel.bone(parent).parent;
    }
  }

  return torso;
}

function computeLateral(
  ctx: AugmentationContext,
): [Vector3, Vector3, number] {
  const skel = ctx.skel;

  const pelvisName = (ctx.chains['Pelvis'] ?? [null])[0];
  let pelvisPos: Vector3;
  if (pelvisName !== null) {
    const pelvisIdx = ctx.boneIndex(pelvisName);
    if (pelvisIdx !== null) {
      const e = skel.bone(pelvisIdx).restWorld.elements;
      pelvisPos = new Vector3(e[12], e[13], e[14]);
    } else {
      pelvisPos = new Vector3();
    }
  } else {
    pelvisPos = new Vector3();
  }

  let lateralAxis = new Vector3(1.0, 0.0, 0.0);
  let leftHandPos: Vector3 | null = null;
  let rightHandPos: Vector3 | null = null;

  if ('LeftArm' in ctx.chains && ctx.chains['LeftArm'].length > 0) {
    const idx = ctx.boneIndex(ctx.chains['LeftArm'][ctx.chains['LeftArm'].length - 1]);
    if (idx !== null) {
      const e = skel.bone(idx).restWorld.elements;
      leftHandPos = new Vector3(e[12], e[13], e[14]);
    }
  }
  if ('RightArm' in ctx.chains && ctx.chains['RightArm'].length > 0) {
    const idx = ctx.boneIndex(ctx.chains['RightArm'][ctx.chains['RightArm'].length - 1]);
    if (idx !== null) {
      const e = skel.bone(idx).restWorld.elements;
      rightHandPos = new Vector3(e[12], e[13], e[14]);
    }
  }

  if (leftHandPos !== null && rightHandPos !== null) {
    const diff = rightHandPos.clone().sub(leftHandPos);
    const norm = diff.length();
    if (norm > 1e-8) lateralAxis = diff.divideScalar(norm);
  }

  const allPositions = skel.bones.map((b) => {
    const e = b.restWorld.elements;
    return new Vector3(e[12], e[13], e[14]);
  });
  const minBounds = allPositions[0].clone();
  const maxBounds = allPositions[0].clone();
  for (const pos of allPositions) {
    minBounds.min(pos);
    maxBounds.max(pos);
  }
  const extent = maxBounds.clone().sub(minBounds);
  const absLateral = new Vector3(
    Math.abs(lateralAxis.x),
    Math.abs(lateralAxis.y),
    Math.abs(lateralAxis.z),
  );
  const lateralExtent = extent.dot(absLateral);

  return [lateralAxis, pelvisPos, lateralExtent];
}

function registerExtraArm(
  ctx: AugmentationContext,
  candidate: ExtraArmCandidate,
  setNumber: number,
): void {
  const skel = ctx.skel;
  const side = candidate.side;
  const suffix = `_${setNumber}`;

  const armChainName = `${side}Arm${suffix}`;
  const clavChainName = `${side}Clavicle${suffix}`;

  const armBoneNames = candidate.armChain.map((idx) => skel.refForBoneIndex(idx));

  ctx.chains[armChainName] = armBoneNames;
  ctx.chainResults[armChainName] = new ChainMapping({
    name: armChainName,
    startBone: armBoneNames[0],
    endBone: armBoneNames[armBoneNames.length - 1],
    boneNames: armBoneNames,
    confidence: ctx.baseConfidence,
  });
  ctx.chainMappings[armChainName] = [
    candidate.armChain[0],
    candidate.armChain[candidate.armChain.length - 1],
    null,
  ];

  if (candidate.clavicleIdx !== null) {
    const clavName = skel.refForBoneIndex(candidate.clavicleIdx);
    ctx.chains[clavChainName] = [clavName];
    ctx.chainResults[clavChainName] = new ChainMapping({
      name: clavChainName,
      startBone: clavName,
      endBone: clavName,
      boneNames: [clavName],
      confidence: ctx.baseConfidence,
    });
    ctx.chainMappings[clavChainName] = [
      candidate.clavicleIdx,
      candidate.clavicleIdx,
      null,
    ];
    ctx.assignRole(`${side}Clavicle${suffix}`, clavName);
  }

  ctx.assignRole(`${side}UpperArm${suffix}`, armBoneNames[0]);
  ctx.assignRole(`${side}Hand${suffix}`, armBoneNames[armBoneNames.length - 1]);

  if (armBoneNames.length >= 3) {
    const mid = Math.floor(armBoneNames.length / 2);
    ctx.assignRole(`${side}LowerArm${suffix}`, armBoneNames[mid]);
  }
}

function detectFingersOnExtraHand(
  ctx: AugmentationContext,
  handBoneName: string,
  side: string,
  setNumber: number,
): void {
  const skel = ctx.skel;
  const suffix = `_${setNumber}`;
  const handIdx = ctx.boneIndex(handBoneName);
  if (handIdx === null) return;

  const handBone = skel.bone(handIdx);
  if (handBone.children.length < 2) return;

  const fingerConfig = new RecognizerConfig({ detectFingers: true });

  const [fingerChains] = detectFingers(skel, handIdx, side, fingerConfig);

  for (const [fingerName, fingerChain] of Object.entries(fingerChains)) {
    const extraFingerName = `${fingerName}${suffix}`;
    const boneNames = fingerChain.map((idx: number) => skel.refForBoneIndex(idx));
    ctx.chains[extraFingerName] = boneNames;
    ctx.chainResults[extraFingerName] = new ChainMapping({
      name: extraFingerName,
      startBone: boneNames[0],
      endBone: boneNames[boneNames.length - 1],
      boneNames,
      confidence: ctx.baseConfidence,
    });
    ctx.chainMappings[extraFingerName] = [
      fingerChain[0],
      fingerChain[fingerChain.length - 1],
      null,
    ];

    for (const { boneIndex, roleName } of getChainRoleAssignments(
      extraFingerName,
      fingerChain.length,
    )) {
      const boneIdx = fingerChain[boneIndex];
      if (boneIdx == null) continue;
      ctx.assignRole(roleName, skel.refForBoneIndex(boneIdx));
    }
  }
}

function computeUpAxis(ctx: AugmentationContext): Vector3 {
  const skel = ctx.skel;
  const legChains: number[][] = [];
  for (const side of ['Left', 'Right']) {
    const legName = `${side}Leg`;
    if (legName in ctx.chains && ctx.chains[legName].length > 0) {
      const indices = ctx.chains[legName]
        .map((n) => ctx.boneIndex(n))
        .filter((i): i is number => i !== null);
      if (indices.length > 0) legChains.push(indices);
    }
  }

  if (legChains.length > 0) {
    const dirs: Vector3[] = [];
    for (const chain of legChains) {
      if (chain.length < 2) continue;
      const startE = skel.bone(chain[0]).restWorld.elements;
      const endE = skel.bone(chain[chain.length - 1]).restWorld.elements;
      const dir = new Vector3(
        endE[12] - startE[12],
        endE[13] - startE[13],
        endE[14] - startE[14],
      );
      const n = dir.length();
      if (n > 1e-8) dirs.push(dir.divideScalar(n));
    }
    if (dirs.length > 0) {
      const avg = new Vector3();
      for (const d of dirs) avg.add(d);
      avg.divideScalar(dirs.length);
      const n = avg.length();
      if (n > 1e-8) return avg.divideScalar(n).negate();
    }
  }

  // Fallback: derive from bounding box extent (same as spatial.ts)
  const positions = skel.bones.map((b) => {
    const e = b.restWorld.elements;
    return new Vector3(e[12], e[13], e[14]);
  });
  if (positions.length === 0) return new Vector3(0, 1, 0);
  const minB = positions[0].clone();
  const maxB = positions[0].clone();
  for (const p of positions) { minB.min(p); maxB.max(p); }
  const ext = maxB.clone().sub(minB);
  const extArr = [ext.x, ext.y, ext.z];
  const axisOrder = [0, 1, 2].sort((a, b) => extArr[b] - extArr[a]);
  if (axisOrder[0] === 2) return new Vector3(0, 0, 1);
  return new Vector3(0, 1, 0);
}

function detectExtraArms(ctx: AugmentationContext): void {
  const torsoIndices = collectTorsoIndices(ctx);
  if (torsoIndices.length === 0) return;

  const claimed = collectClaimedIndices(ctx);
  const [lateralAxis, pelvisPos, lateralExtent] = computeLateral(ctx);
  const upAxis = computeUpAxis(ctx);

  if (lateralExtent < 1e-6) return;

  const candidates = findExtraArmCandidates(
    ctx.skel,
    torsoIndices,
    claimed,
    lateralAxis,
    pelvisPos,
    lateralExtent,
    upAxis,
  );

  if (candidates.length === 0) return;

  const leftCandidates: ExtraArmCandidate[] = [];
  const rightCandidates: ExtraArmCandidate[] = [];
  for (const c of candidates) {
    if (c.side === 'Left') leftCandidates.push(c);
    else rightCandidates.push(c);
  }

  const maxExtraSets = 4;
  const assignments: [number, ExtraArmCandidate][] = [];
  const usedLeft = new Array(leftCandidates.length).fill(false);
  const usedRight = new Array(rightCandidates.length).fill(false);

  let nextSet = 2;

  for (let li = 0; li < leftCandidates.length; li++) {
    if (nextSet > maxExtraSets + 1) break;
    let bestRi: number | null = null;
    let bestDist: number | null = null;
    for (let ri = 0; ri < rightCandidates.length; ri++) {
      if (usedRight[ri]) continue;
      const dist = Math.abs(
        leftCandidates[li].spineParentIdx - rightCandidates[ri].spineParentIdx,
      );
      if (bestDist === null || dist < bestDist) {
        bestDist = dist;
        bestRi = ri;
      }
    }
    if (bestRi !== null && bestDist !== null && bestDist <= 1) {
      usedLeft[li] = true;
      usedRight[bestRi] = true;
      assignments.push([nextSet, leftCandidates[li]]);
      assignments.push([nextSet, rightCandidates[bestRi]]);
      nextSet++;
    }
  }

  for (let li = 0; li < leftCandidates.length; li++) {
    if (nextSet > maxExtraSets + 1) break;
    if (!usedLeft[li]) {
      assignments.push([nextSet, leftCandidates[li]]);
      nextSet++;
    }
  }

  for (let ri = 0; ri < rightCandidates.length; ri++) {
    if (nextSet > maxExtraSets + 1) break;
    if (!usedRight[ri]) {
      assignments.push([nextSet, rightCandidates[ri]]);
      nextSet++;
    }
  }

  const registeredSets = new Set<number>();
  for (const [setNumber, cand] of assignments) {
    registerExtraArm(ctx, cand, setNumber);

    const handName = ctx.skel.refForBoneIndex(
      cand.armChain[cand.armChain.length - 1],
    );
    detectFingersOnExtraHand(ctx, handName, cand.side, setNumber);
    registeredSets.add(setNumber);
  }

  const totalExtraSets = registeredSets.size;
  if (totalExtraSets > 0) {
    const label = totalExtraSets === 1 ? 'set' : 'sets';
    ctx.addFeature(
      `Extra Arms (${totalExtraSets} extra ${label})`,
      `Detected ${totalExtraSets} additional ${label} of arms ` +
        `branching from the spine/torso, producing ` +
        `${totalExtraSets + 1} total arm sets.`,
    );
  }
}

// ═══════════════════════════════════════════════════════════════════════
// APPENDAGES AUGMENTATION
// ═══════════════════════════════════════════════════════════════════════

interface AppendageChain {
  chainName: string;
  appendageType: AppendageType;
  boneIndices: number[];
  boneNames: string[];
  startRole: string;
  endRole: string;
}

function isContainerLikeName(name: string): boolean {
  const lower = name.toLowerCase();
  return (
    lower.includes('group') ||
    lower.endsWith('_grp') ||
    lower.startsWith('grp_') ||
    lower.includes('_grp_')
  );
}

function collectClaimedIndicesFromChains(
  skel: Skeleton,
  chains: Record<string, string[]>,
): Set<number> {
  const claimed = new Set<number>();
  for (const chainBones of Object.values(chains)) {
    for (const bname of chainBones) {
      const idx = skel.resolveBoneRef(bname, { namespace: 'internal' });
      if (idx !== null) claimed.add(idx);
    }
  }
  return claimed;
}

function walkChainFrom(
  skel: Skeleton,
  startIdx: number,
  claimed: Set<number>,
  appendageType: AppendageType | null = null,
  maxBones = 20,
): number[] {
  const chain = [startIdx];
  let current = startIdx;

  for (let i = 0; i < maxBones - 1; i++) {
    const preferCandidate =
      appendageType === null
        ? undefined
        : (boneIdx: number) =>
            matchAppendageTypeFromName(skel.bone(boneIdx).name) === appendageType;
    const { boneIdx: nextBone, matchedPreference } = findNextStructuralBone(
      skel,
      current,
      preferCandidate,
    );
    if (nextBone === null) break;
    if (claimed.has(nextBone)) break;
    const currentNonAuxChildren = skel.bone(current).children.filter(
      (c) => !skel.bone(c).auxiliary,
    );
    if (
      appendageType !== null &&
      currentNonAuxChildren.length >= 2 &&
      !matchedPreference
    ) {
      break;
    }

    chain.push(nextBone);
    current = nextBone;

    const nonAux = skel.bone(nextBone).children.filter((c) => !skel.bone(c).auxiliary);
    if (appendageType === null && nonAux.length >= 3) break;
  }

  if (appendageType !== null) {
    const lastIdx = chain[chain.length - 1];
    const hints = APPENDAGE_HINTS[appendageType] ?? [];
    for (const childIdx of skel.bone(lastIdx).children) {
      const child = skel.bone(childIdx);
      if (
        child.auxiliary &&
        child.children.length === 0 &&
        !claimed.has(childIdx) &&
        hints.some((h) => child.name.toLowerCase().includes(h))
      ) {
        chain.push(childIdx);
        break;
      }
    }
  }

  return chain;
}

function findAppendageRoot(
  skel: Skeleton,
  boneIdx: number,
  appendageType: AppendageType,
  claimed: Set<number>,
): number {
  const hints = APPENDAGE_HINTS[appendageType];
  let root = boneIdx;
  let current = boneIdx;

  while (true) {
    const parent = skel.bone(current).parent;
    if (parent === null) break;
    if (claimed.has(parent)) break;
    const parentBone = skel.bone(parent);
    if (parentBone.auxiliary) break;
    if (isContainerLikeName(parentBone.name)) break;
    if (!hints.some((h) => parentBone.name.toLowerCase().includes(h))) break;
    root = parent;
    current = parent;
  }

  return root;
}

function isShortTailChainAttachedToClaimedBody(
  skel: Skeleton,
  rootIdx: number,
  chainIndices: number[],
  claimed: Set<number>,
): boolean {
  if (chainIndices.length !== 2) return false;

  let parent = skel.bone(rootIdx).parent;
  while (parent !== null) {
    if (claimed.has(parent)) return true;
    const parentBone = skel.bone(parent);
    if (!parentBone.auxiliary) return false;
    parent = parentBone.parent;
  }

  return false;
}

export function findAppendageChains(
  skel: Skeleton,
  chains: Record<string, string[]>,
): AppendageChain[] {
  const claimed = collectClaimedIndicesFromChains(skel, chains);
  const results: AppendageChain[] = [];
  const typeCounts: Record<string, number> = {};
  const processedRoots = new Set<number>();

  for (const bone of skel.bones) {
    if (bone.auxiliary) continue;
    if (claimed.has(bone.index)) continue;
    if (isContainerLikeName(bone.name)) continue;

    const appendageType = matchAppendageTypeFromName(bone.name);
    if (appendageType === null) continue;

    const rootIdx = findAppendageRoot(skel, bone.index, appendageType, claimed);
    if (processedRoots.has(rootIdx)) continue;
    processedRoots.add(rootIdx);

    const chainIndices = walkChainFrom(skel, rootIdx, claimed, appendageType);

    const spec = CHAIN_DEFINITIONS[appendageType];
    const minBones = spec ? spec.minBones : 3;
    const allowShortTail =
      appendageType === 'Tail' &&
      isShortTailChainAttachedToClaimedBody(skel, rootIdx, chainIndices, claimed);
    if (chainIndices.length < minBones && !allowShortTail) continue;

    const count = (typeCounts[appendageType] ?? 0) + 1;
    typeCounts[appendageType] = count;

    const chainName = count === 1 ? appendageType : `${appendageType}_${count}`;
    const boneNames = chainIndices.map((idx) => skel.refForBoneIndex(idx));

    let startRole = `${appendageType}Start`;
    let endRole = `${appendageType}End`;
    if (count > 1) {
      startRole = `${appendageType}Start_${count}`;
      endRole = `${appendageType}End_${count}`;
    }

    results.push({
      chainName,
      appendageType,
      boneIndices: chainIndices,
      boneNames,
      startRole,
      endRole,
    });

    for (const idx of chainIndices) claimed.add(idx);
  }

  return results;
}

function detectAppendages(ctx: AugmentationContext): void {
  const detected = findAppendageChains(ctx.skel, ctx.chains);

  for (const ap of detected) {
    ctx.chains[ap.chainName] = ap.boneNames;
    ctx.chainResults[ap.chainName] = new ChainMapping({
      name: ap.chainName,
      startBone: ap.boneNames[0],
      endBone: ap.boneNames[ap.boneNames.length - 1],
      boneNames: ap.boneNames,
      confidence: ctx.baseConfidence,
    });
    ctx.chainMappings[ap.chainName] = [
      ap.boneIndices[0],
      ap.boneIndices[ap.boneIndices.length - 1],
      null,
    ];

    ctx.assignRole(ap.startRole, ap.boneNames[0]);
    ctx.assignRole(ap.endRole, ap.boneNames[ap.boneNames.length - 1]);
  }

  const typeCounts: Record<string, number> = {};
  for (const ap of detected) {
    typeCounts[ap.appendageType] = (typeCounts[ap.appendageType] ?? 0) + 1;
  }

  for (const [appendageType, count] of Object.entries(typeCounts)) {
    if (count === 1) {
      const chain = ctx.chains[appendageType] ?? [];
      ctx.addFeature(
        appendageType,
        `Detected ${appendageType.toLowerCase()} chain (${chain.length} bones).`,
      );
    } else {
      ctx.addFeature(
        `${appendageType} (${count})`,
        `Detected ${count} ${appendageType.toLowerCase()} chains.`,
      );
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════
// RUNNER
// ═══════════════════════════════════════════════════════════════════════

type AugmentationFn = (ctx: AugmentationContext) => void;

const AUGMENTATIONS: AugmentationFn[] = [
  detectDigitigradeLegs,
  detectExtraArms,
  detectAppendages,
];

export function runAllAugmentations(ctx: AugmentationContext): void {
  for (const augmentation of AUGMENTATIONS) {
    augmentation(ctx);
  }
}
